import type { RouteRecordName } from 'vue-router'
import { CUSTOMER_ROUTE_LOADERS_BY_NAME, CustomerRoot } from '@/router/customerRoutes'

let shellPrefetchStarted = false
let idleSecondaryScheduled = false
let tabVisiblePrefetchInitialized = false
let tabVisiblePrefetchHandler: (() => void) | null = null
/** Incremented on full disposal so in-flight prefetch work from a prior session cannot mutate state. */
let prefetchSession = 0

const inflight = new Map<string, Promise<unknown>>()
const succeeded = new Set<string>()

/**
 * Start loading the customer layout + home in parallel, then (when idle) other main nav chunks.
 * Idempotent per page load. Call when an authenticated customer session is ready.
 */
export function prefetchCustomerShell(): void {
  if (shellPrefetchStarted) {
    return
  }
  shellPrefetchStarted = true
  const session = prefetchSession

  const homeLoader = CUSTOMER_ROUTE_LOADERS_BY_NAME['home']
  void Promise.all([CustomerRoot(), homeLoader ? homeLoader() : Promise.resolve()])
    .then(() => {
      if (session !== prefetchSession) {
        return
      }
      scheduleIdleSecondaryNavPrefetch()
    })
    .catch(() => {
      // Failed prefetch should not block real navigation; allow retry via gestures.
      if (session !== prefetchSession) {
        return
      }
      shellPrefetchStarted = false
    })
}

function secondaryNavRouteNames(): string[] {
  return Object.keys(CUSTOMER_ROUTE_LOADERS_BY_NAME).filter((n) => n !== 'home')
}

function runSecondaryNavPrefetch(): void {
  for (const name of secondaryNavRouteNames()) {
    prefetchCustomerNavRoute(name)
  }
}

/**
 * Secondary chunks after shell + home — deferred so we do not compete with first paint.
 * @param force When true, schedule idle work even if already scheduled once (e.g. tab visible again
 *   after background — idle may have been starved; re-warm must not no-op).
 */
function scheduleIdleSecondaryNavPrefetch(force = false): void {
  if (!force && idleSecondaryScheduled) {
    return
  }
  idleSecondaryScheduled = true
  const session = prefetchSession

  const run = (): void => {
    if (session !== prefetchSession) {
      return
    }
    runSecondaryNavPrefetch()
  }

  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(run, { timeout: 5000 })
  } else {
    window.setTimeout(run, 2000)
  }
}

/**
 * When the tab becomes visible again, warm remaining nav chunks (idle may have been starved in background).
 */
export function initCustomerRoutePrefetchOnTabVisible(): void {
  if (tabVisiblePrefetchInitialized || typeof document === 'undefined') {
    return
  }
  tabVisiblePrefetchInitialized = true

  const onVisible = (): void => {
    if (document.visibilityState !== 'visible') {
      return
    }
    scheduleIdleSecondaryNavPrefetch(true)
  }

  tabVisiblePrefetchHandler = onVisible
  document.addEventListener('visibilitychange', onVisible)
  onVisible()
}

/**
 * Removes the tab-visibility listener and resets all customer prefetch state (flags + tracking maps).
 * Safe to call if init was never run or already disposed — still clears session-scoped state so a new
 * user session can re-run prefetch without a full reload.
 */
export function disposeCustomerRoutePrefetchOnTabVisible(): void {
  if (tabVisiblePrefetchHandler && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', tabVisiblePrefetchHandler)
  }
  tabVisiblePrefetchHandler = null
  tabVisiblePrefetchInitialized = false
  shellPrefetchStarted = false
  idleSecondaryScheduled = false
  succeeded.clear()
  inflight.clear()
  prefetchSession++
}

/**
 * Clears session-scoped prefetch state and removes the tab-visibility listener.
 * Call when the customer session ends (logout, role change, or unauthenticated) so a later
 * customer session can run prefetch again without a full reload.
 */
export function resetCustomerRoutePrefetchState(): void {
  disposeCustomerRoutePrefetchOnTabVisible()
}

/**
 * Prefetch a customer page by Vue Router route `name` (navbar items).
 * Safe to call on hover, focus, or touchstart.
 */
export function prefetchCustomerNavRoute(routeName: RouteRecordName | undefined | null): void {
  if (routeName == null || typeof routeName !== 'string') {
    return
  }

  const loader = CUSTOMER_ROUTE_LOADERS_BY_NAME[routeName]
  if (!loader) {
    return
  }

  if (succeeded.has(routeName) || inflight.has(routeName)) {
    return
  }

  const session = prefetchSession
  const p = loader()
  inflight.set(routeName, p)
  void p
    .then(() => {
      if (session !== prefetchSession) {
        return
      }
      succeeded.add(routeName)
    })
    .catch(() => {
      // Allow retry on the next gesture if the network blipped.
    })
    .finally(() => {
      if (session !== prefetchSession) {
        return
      }
      inflight.delete(routeName)
    })
}
